/**
 * Bulk-transfer dialog for the site-group grid's selection bar — picks the
 * destination customer for the selected groups (and, with them, all of their
 * sites), then states exactly what the move will do before it is committed.
 *
 * Moving a group across a tenancy boundary has consequences the operator
 * cannot see from the grid: sites follow the group, people without membership
 * under the destination lose their group membership, and shares to an outside
 * customer stop being backed by any agreement. The dialog dry-runs the
 * transfer as soon as a destination is chosen and spells those out, so
 * nothing destructive happens on an unread confirm. Where a share would be
 * left unbacked the operator must say which way to resolve it — drop the
 * shares, or grant the destination the missing agreements — before Transfer
 * enables.
 *
 * Group membership has no equivalent auto-grant, because widening one
 * person's reach means widening their whole customer's. A membership without
 * customer access is an unintended state, so members who cannot reach the
 * destination are always removed from the moved groups; the dialog names
 * them so the operator knows who to re-add once their access is set up.
 *
 * Contains:
 * - SHARE_RESOLUTION — the two ways to resolve unbacked shares
 * - TransferSiteGroupsDialog — the dialog (default export)
 */
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import CustomerAutocomplete from '@sophocles/components/CustomerAutocomplete';
import { usePreflightTransferSiteGroupsMutation } from '@sophocles/features/entities/siteGroups';
import { selectTenantInfo } from '@sophocles/features/tenant/slice';
import useSiteGroupTerm from '@sophocles/hooks/useSiteGroupTerm';
import useSiteTerm from '@sophocles/hooks/useSiteTerm';

export const SHARE_RESOLUTION = Object.freeze({ REMOVE: 'remove', GRANT: 'grant' });

// A bulk transfer can strand hundreds of members; the name list scrolls past
// this height so the alert never grows with the member count.
const MEMBER_LIST_MAX_HEIGHT = 148;

/**
 * @param {object} props
 * @param {boolean} props.open - Whether the dialog is mounted open.
 * @param {Function} props.onClose - Called when the operator cancels.
 * @param {Function} props.onConfirm - Called with
 *   `(targetCustomerId, { shareResolution })` once the operator commits.
 * @param {number} props.count - How many groups are in the selection.
 * @param {number} props.currentCustomerId - The groups' current customer, used
 *   to block a no-op transfer into where they already are.
 * @param {Array<number>} [props.siteGroupIds] - Explicit selection, when not select-all.
 * @param {boolean} [props.selectAll] - Whether the selection is the whole filter scope.
 * @param {object} [props.filters] - The grid's JSON:API param dict, for select-all.
 * @returns {React.ReactElement} The dialog.
 */
export default function TransferSiteGroupsDialog({
  open,
  onClose,
  onConfirm,
  count,
  currentCustomerId,
  siteGroupIds,
  selectAll,
  filters,
}) {
  const { t } = useTranslation();
  const siteGroupTerm = useSiteGroupTerm();
  const siteTerm = useSiteTerm();
  const { customer_term: customerTerm } = useSelector(selectTenantInfo);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [destinationName, setDestinationName] = useState(null);
  const [shareResolution, setShareResolution] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [preflightFailed, setPreflightFailed] = useState(false);

  const [runPreflight, { isLoading: isPreflighting }] = usePreflightTransferSiteGroupsMutation();

  useEffect(() => {
    if (!open) return;
    setSelectedCustomerId(null);
    setDestinationName(null);
    setShareResolution(null);
    setPreflight(null);
    setPreflightFailed(false);
  }, [open]);

  // Dry-run as soon as a real destination is picked; the operator reads the
  // consequences before Transfer is ever enabled.
  useEffect(() => {
    if (!open || !selectedCustomerId || selectedCustomerId === currentCustomerId) {
      setPreflight(null);
      setPreflightFailed(false);
      return;
    }
    let cancelled = false;
    setShareResolution(null);
    runPreflight({ siteGroupIds, selectAll, filters, targetCustomerId: selectedCustomerId }).then(result => {
      if (cancelled) return;
      if (result.error) {
        setPreflight(null);
        setPreflightFailed(true);
        return;
      }
      setPreflightFailed(false);
      setPreflight(result.data);
    });
    return () => {
      cancelled = true;
    };
  }, [open, selectedCustomerId, currentCustomerId, siteGroupIds, selectAll, filters, runPreflight]);

  const groupWord = (n = 2) => (n === 1 ? siteGroupTerm.singular.toLowerCase() : siteGroupTerm.plural.toLowerCase());
  const siteWord = n => (n === 1 ? siteTerm.singular.toLowerCase() : siteTerm.plural.toLowerCase());
  const destination =
    destinationName ??
    t('siteGroups.transferDestinationFallback', 'the destination {{customer}}', {
      customer: customerTerm.toLowerCase(),
    });

  const collidingNames = preflight?.colliding_names ?? [];
  const unreachableUsers = preflight?.unreachable_users ?? [];
  const unrelatedThirdParties = preflight?.unrelated_third_parties ?? [];
  const needsShareChoice = unrelatedThirdParties.length > 0;
  const movingCount = preflight?.transferable?.length ?? 0;
  const movedSiteCount = preflight?.moved_site_count ?? 0;
  const skippedCount = preflight?.skipped?.length ?? 0;
  const severedShareCount = preflight?.severed_share_count ?? 0;
  const severedAutoShareCount = preflight?.severed_auto_share_count ?? 0;
  const removalDetail = [
    ...(severedShareCount > 0
      ? [t('siteGroups.transferShareCount', '{{count}} shares', { count: severedShareCount })]
      : []),
    ...(severedAutoShareCount > 0
      ? [t('siteGroups.transferAutoShareCount', '{{count}} auto-share rules', { count: severedAutoShareCount })]
      : []),
  ].join(', ');

  const confirmDisabled =
    !selectedCustomerId ||
    selectedCustomerId === currentCustomerId ||
    isPreflighting ||
    preflightFailed ||
    collidingNames.length > 0 ||
    (needsShareChoice && !shareResolution);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {count
          ? t('siteGroups.transferCountTerm', 'Transfer {{count}} {{term}}', {
              count,
              term: groupWord(count),
            })
          : t('siteGroups.transferTerm', 'Transfer {{term}}', { term: siteGroupTerm.plural.toLowerCase() })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <CustomerAutocomplete
            fullWidth
            label={t('siteGroups.destinationCustomer', 'Destination {{customer}}', { customer: customerTerm })}
            value={selectedCustomerId}
            onChange={(e, nextCustomerId) => setSelectedCustomerId(nextCustomerId)}
            onSelectedChange={option => setDestinationName(option.label)}
          />

          {isPreflighting && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {t('siteGroups.transferChecking', 'Checking this transfer…')}
              </Typography>
            </Stack>
          )}

          {preflightFailed && (
            <Alert severity="error">
              {t('siteGroups.transferPreflightFailed', 'Could not check this transfer. Try again.')}
            </Alert>
          )}

          {preflight && collidingNames.length > 0 && (
            <Alert severity="error">
              <AlertTitle>{t('siteGroups.transferBlocked', 'This transfer cannot run')}</AlertTitle>
              {t(
                'siteGroups.transferNameCollision',
                '{{destination}} already uses these names (inactive ones count): {{names}}. Rename them first.',
                { destination, names: collidingNames.join(', ') },
              )}
            </Alert>
          )}

          {preflight && collidingNames.length === 0 && (
            <Alert severity="info">
              <Typography variant="body2">
                {t('siteGroups.transferSummary', 'Moves {{groups}} and {{sites}} to {{destination}}.', {
                  groups: `${movingCount} ${groupWord(movingCount)}`,
                  sites: `${movedSiteCount} ${siteWord(movedSiteCount)}`,
                  destination,
                })}
              </Typography>
              {skippedCount > 0 && (
                <Typography variant="body2">
                  {t('siteGroups.transferSummarySkipped', 'Skips {{count}} already there.', { count: skippedCount })}
                </Typography>
              )}
            </Alert>
          )}

          {preflight && collidingNames.length === 0 && unreachableUsers.length > 0 && (
            <Alert severity="warning">
              <AlertTitle>
                {t('siteGroups.transferUsersTitle', '{{count}} members will be removed', {
                  count: unreachableUsers.length,
                })}
              </AlertTitle>
              <Typography variant="body2">
                {t(
                  'siteGroups.transferUsersExplain',
                  'These members have no membership with {{destination}}, so their membership in the moved {{term}} will be removed. Re-add them once they are given access to {{destination}}.',
                  { term: groupWord(), destination },
                )}
              </Typography>
              <Box component="ul" sx={{ maxHeight: MEMBER_LIST_MAX_HEIGHT, overflowY: 'auto', pl: 2.5, mt: 1, mb: 0 }}>
                {unreachableUsers.map(member => (
                  <Typography key={member.id} component="li" variant="body2">
                    {member.customer ? `${member.name} (${member.customer.name})` : member.name}
                  </Typography>
                ))}
              </Box>
            </Alert>
          )}

          {preflight && collidingNames.length === 0 && needsShareChoice && (
            <Alert severity="warning">
              <AlertTitle>{t('siteGroups.transferSharesTitle', 'Shared outside the destination')}</AlertTitle>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('siteGroups.transferSharesExplain', '{{destination}} has no sharing agreement with: {{names}}.', {
                  destination,
                  names: unrelatedThirdParties.map(tp => tp.name).join(', '),
                })}
              </Typography>
              <RadioGroup value={shareResolution ?? ''} onChange={e => setShareResolution(e.target.value)}>
                <FormControlLabel
                  value={SHARE_RESOLUTION.REMOVE}
                  control={<Radio size="small" />}
                  label={t(
                    'siteGroups.transferSharesRemove',
                    'End all sharing on the moved {{term}} ({{removed}} removed)',
                    { term: groupWord(), removed: removalDetail },
                  )}
                />
                <FormControlLabel
                  value={SHARE_RESOLUTION.GRANT}
                  control={<Radio size="small" />}
                  label={t('siteGroups.transferSharesGrant', 'Keep the sharing by creating the missing agreements')}
                />
              </RadioGroup>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.cancel', 'Cancel')}</Button>
        <Button
          onClick={() => onConfirm(selectedCustomerId, { shareResolution })}
          variant="contained"
          disabled={confirmDisabled}
        >
          {t('actions.transfer', 'Transfer')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
